// Ultimate Card Game (UCG) - Bot AI superweapon controller ("Nuke 2.0")
//
// The superweapon wipes EVERY unit on the field, both sides, after a short
// flight (SUPERWEAPON_IMPACT_DELAY); the target point is cosmetic. So firing
// is purely a value-and-timing decision: is the enemy board worth so much
// more than ours that a reset helps us - and will we be back first? The old
// threshold heuristic answered neither and measurably gained nothing (see
// docs/AI_OPPONENT_ANALYSIS_AND_PLAN_2026-09-22.md section 4); this one
// follows docs/AI_OPPONENT_IMPLEMENTATION_PLAN_2026-09-22.md Phase 1.
//
// States:  WATCH -> ARMING (bank plasma) -> fire -> HOLD (no plays until
//          impact) -> FLOOD (one max-budget burst) -> SPENT
//
// Per decision tick BotAI asks tick() for a plan:
//   { mode: 'fire', x, y }     spend the charge and trigger now
//   { mode: 'hold' }           our warhead is in flight - draw only
//   { mode: 'flood' }          impact happened - dump the bank
//   { mode: 'normal', reserve } play normally, but keep `reserve` plasma back
//
// No player-visible tell while arming (design decision E4): this module
// never touches audio, taunts or anything the renderer/HUD reads.
//
// Values only count what the bot may know: undetected cloaked enemies are
// invisible to it, and golden statues (Looting Vulture) are skipped because
// the wipe cannot destroy them.

import { SUPERWEAPON_IMPACT_DELAY } from '../../data/balance.js';
import { BoardView, computeUnitTacticalValue, enemyTeamOf } from './perception.js';
import { describeBoard, estimateBattle } from './battleEstimator.js';

export const NUKE_STATE = Object.freeze({
  WATCH: 'watch',
  ARMING: 'arming',
  HOLD: 'hold',
  FLOOD: 'flood',
  SPENT: 'spent'
});

// Radius of the cosmetic target cluster.
const CLUSTER_RADIUS_PX = 120;
// Hysteresis below the arming swing before banking is released again.
const ARMING_RELEASE_MARGIN = 10;
// Extra hold after impact so a play in the very impact tick can't be wiped.
const HOLD_MARGIN_S = 0.15;
// Consecutive ticks a firing condition must hold (filters one-tick noise).
const CONFIRM_TICKS = 2;

// Re-exported for existing importers; the function moved to perception.js
// with the other board scanning.
export { computeUnitTacticalValue };

// Estimator swing (`nuke.estimator`; `true` takes the defaults): instead of
// the plain plasma value the enemy holds minus ours, the value each side is
// still expected to hold once the fight that is on has played out (Lanchester
// remainders from js/engine/ai/battleEstimator.js). A wipe costs us our units
// even if they would have died anyway; the plain difference counts that
// against us, this does not.
//   horizon  seconds an arriving unit may need to join that fight
//   blend    0 = plain swing, 1 = estimator swing, in between = mix
export const NUKE_ESTIMATOR_DEFAULTS = Object.freeze({ horizon: 10, blend: 1 });

// The share of a side's force that survives a fight at strength ratio
// `ratio` (winner / loser by the square law); the loser keeps nothing.
export function lanchesterRemaining(ratio) {
  if (ratio >= 1) return { own: Math.sqrt(Math.max(0, 1 - 1 / ratio)), enemy: 0 };
  return { own: 0, enemy: Math.sqrt(Math.max(0, 1 - ratio)) };
}

export class NukeController {
  constructor(bot, profile) {
    this.bot = bot;
    this.profile = profile;
    this.reset();
  }

  setProfile(profile) {
    if (profile && profile.estimator) {
      const given = profile.estimator === true ? {} : profile.estimator;
      profile = { ...profile, estimator: { ...NUKE_ESTIMATOR_DEFAULTS, ...given } };
    }
    this.profile = profile;
  }

  reset() {
    this.state = NUKE_STATE.WATCH;
    this.confirmStreak = 0;
    this.holdUntil = 0;
    this.lastAssessment = null;
  }

  get enemyTeam() {
    return enemyTeamOf(this.bot.team);
  }

  // Board picture from the bot's point of view. Pure read, no side effects.
  // `view`: this tick's BoardView (BotAI.makeCardDecision builds one and
  // hands it to every consumer); omitted by direct callers, who scan afresh.
  assess(view = null) {
    const { battlefield, economy, team, superweapons } = this.bot;
    const enemy = this.enemyTeam;
    const ownBase = battlefield.bases?.[team];
    const enemyBase = battlefield.bases?.[enemy];
    const t = (view || new BoardView(this.bot)).tactical;

    const baseHpFraction = ownBase ? ownBase.hp / (ownBase.maxHp || 1) : 1;
    const enemyBaseHpFraction = enemyBase ? enemyBase.hp / (enemyBase.maxHp || 1) : 1;
    const ownPlasma = economy.resources[team]?.plasma || 0;
    const enemyPlasma = (this.bot.belief ? this.bot.belief.enemyPlasma() : economy.resources[enemy]?.plasma) || 0;

    let swing = t.enemyValue - t.ownValue;
    const plainSwing = swing;
    const e = this.profile.estimator;
    if (e) {
      const { own, enemy } = describeBoard(battlefield, team);
      const ratio = estimateBattle(own, enemy, { ownTeam: team, bases: battlefield.bases, params: { horizon: e.horizon } }).ratio;
      const left = lanchesterRemaining(ratio);
      let ownVal = 0, enemyVal = 0;
      for (const r of own) ownVal += r.value;
      for (const r of enemy) if (!r.hidden) enemyVal += r.value;
      const estSwing = enemyVal * left.enemy - ownVal * left.own;
      swing = (1 - e.blend) * plainSwing + e.blend * estSwing;
    }
    const refill = ownPlasma - enemyPlasma;
    const danger = t.enemyInOwnHalf * (1 - baseHpFraction);

    return {
      enemyValue: t.enemyValue,
      ownValue: t.ownValue,
      swing,
      plainSwing,
      refill,
      danger,
      enemyNearBase: t.enemyNearBase,
      ownNearBase: t.ownNearBase,
      baseHpFraction,
      enemyBaseHpFraction,
      ownPlasma,
      enemyNukeLive: superweapons.get(enemy) > 0,
      visibleEnemies: t.visibleEnemies,
      enemyParachutingCount: t.enemyParachutingCount,
      enemyParachutingValue: t.enemyParachutingValue,
      hasEnemyParachuting: t.enemyParachutingCount > 0
    };
  }

  // Dynamic firing threshold: the profile's base, shifted by the opponent's
  // superweapon state and by how late the match is.
  threshold(a) {
    const p = this.profile;
    let theta = p.theta + (this.bot.strategist?.nukeThetaOffset?.() || 0);
    if (p.opponentAware) {
      theta += a.enemyNukeLive ? p.insuranceTheta : -p.lastResetTheta;
    }
    if (p.lateGameStart !== null) {
      let decay = 0;
      const t = this.bot.gameTime;
      if (t > p.lateGameStart) decay = ((t - p.lateGameStart) / 60) * p.lateGameDecayPerMin;
      if (a.enemyBaseHpFraction < p.finisherEnemyBaseHp) decay = p.lateGameMaxDecay;
      theta -= Math.min(p.lateGameMaxDecay, decay);
    }
    return theta;
  }

  utility(a) {
    return a.swing + this.profile.kRefill * a.refill + this.profile.kDanger * a.danger;
  }

  // Imminent base loss: badly damaged and outgunned right at the gate. Fires
  // regardless of banking - there may be no later.
  isCritical(a) {
    return a.baseHpFraction < 0.35 && a.enemyNearBase > 0 && a.enemyNearBase > a.ownNearBase;
  }

  // Plasma to keep back this tick (arming bank and/or insurance).
  reserveFor(a) {
    const p = this.profile;
    let reserve = 0;
    if (this.state === NUKE_STATE.ARMING && p.arming) {
      reserve = a.enemyNukeLive ? p.arming.reserveVsLiveNuke : p.arming.reserve;
    }
    if (p.insurance && a.enemyNukeLive && -a.swing >= p.insurance.lead) {
      reserve = Math.max(reserve, p.insurance.reserve);
    }
    return reserve;
  }

  // Cosmetic aim: the visible enemy with the most value around it, so the
  // strike reads as "that was meant for your army" rather than landing on
  // empty ground between two groups (the old centroid could).
  pickTarget(a) {
    const { battlefield, team } = this.bot;
    let best = null;
    let bestValue = -1;
    const r2 = CLUSTER_RADIUS_PX * CLUSTER_RADIUS_PX;
    const groundedEnemies = a.visibleEnemies.filter(u => !u.isParachuting);
    const candidatePool = groundedEnemies.length > 0 ? groundedEnemies : a.visibleEnemies;
    for (const u of candidatePool) {
      let value = 0;
      for (const v of candidatePool) {
        const dx = v.x - u.x;
        const dy = v.y - u.y;
        if (dx * dx + dy * dy <= r2) value += v.cardData?.cost || 6;
      }
      if (value > bestValue) {
        bestValue = value;
        best = u;
      }
    }
    if (!best) {
      return {
        x: team === 'western' ? 1200 : 400,
        y: battlefield.laneY + battlefield.laneHeight / 2
      };
    }
    return { x: Math.min(1300, Math.max(300, best.x)), y: best.y };
  }

  impactDelay() {
    const race = this.bot.battlefield.bases?.[this.bot.team]?.race === 'Sul' ? 'Sul' : 'Human';
    return SUPERWEAPON_IMPACT_DELAY[race];
  }

  // Called by BotAI right after it actually spent the charge and triggered.
  onFired() {
    this.confirmStreak = 0;
    if (this.profile.hold) {
      this.state = NUKE_STATE.HOLD;
      this.holdUntil = this.bot.gameTime + this.impactDelay() + HOLD_MARGIN_S;
    } else {
      this.state = NUKE_STATE.SPENT;
    }
  }

  tick(view = null) {
    const p = this.profile;
    const gameTime = this.bot.gameTime;

    if (this.state === NUKE_STATE.HOLD) {
      if (gameTime < this.holdUntil) return { mode: 'hold' };
      this.state = p.flood ? NUKE_STATE.FLOOD : NUKE_STATE.SPENT;
    }
    if (this.state === NUKE_STATE.FLOOD) {
      this.state = NUKE_STATE.SPENT;
      return { mode: 'flood' };
    }

    const a = this.assess(view);
    this.lastAssessment = a;
    const hasCharge = this.bot.superweapons.get(this.bot.team) > 0;

    if (!hasCharge) {
      // Spent (by us, or taken away): only the insurance reserve remains.
      if (this.state !== NUKE_STATE.SPENT) this.state = NUKE_STATE.SPENT;
      this.confirmStreak = 0;
      return { mode: 'normal', reserve: this.reserveFor(a) };
    }

    if (gameTime < p.earliest || this.bot.battlefield.isGameOver) {
      this.state = NUKE_STATE.WATCH;
      this.confirmStreak = 0;
      return { mode: 'normal', reserve: this.reserveFor(a) };
    }

    if (p.arming) {
      if (a.swing >= p.arming.swing) this.state = NUKE_STATE.ARMING;
      else if (this.state === NUKE_STATE.ARMING && a.swing < p.arming.swing - ARMING_RELEASE_MARGIN) this.state = NUKE_STATE.WATCH;
    }

    const valueStrike = p.valueStrikes !== false && a.swing >= p.minSwing && this.utility(a) >= this.threshold(a);
    if (valueStrike || this.isCritical(a)) {
      this.confirmStreak++;
      if (this.confirmStreak >= CONFIRM_TICKS) {
        // Parachute Landing Safety Gate (e.g. after enemy casts Fodder):
        // While enemy units are in the air (isParachuting), they are immune to damage (takeDamage returns).
        // If we fire the nuke/bio-plasma while they are descending, they will survive the wipe completely!
        // We MUST wait until all parachuting soldiers have touched down on the ground before triggering.
        if (a.hasEnemyParachuting) {
          return { mode: 'normal', reserve: this.reserveFor(a) };
        }
        return { mode: 'fire', ...this.pickTarget(a) };
      }
    } else {
      this.confirmStreak = 0;
    }
    return { mode: 'normal', reserve: this.reserveFor(a) };
  }
}
